import { type QueryClient, type QueryKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { useSnackbar } from "../../../../../contexts/SnackbarContext";
import { invalidateAllJobNotificationQueries } from "../../../../../hooks/useNurseJobNotifications";
import { backendService } from "../../../../../services/backend.service";
import type {
	CertificationJobMapping,
	CertMappingDeleteResult,
	CertPrimaryJobChangePreview,
	EmployeeStatusMapping,
	EmployeeStatusMappingDeleteResult,
	EmploymentMappingDeleteResult,
	EmploymentTypeMapping,
	HrisEmployeeShiftStats,
	HrisIgnoreCategory,
	HrisIgnoredValueRow,
	HrisPtoTypeStats,
	HrisRawEmployeeRow,
	Job,
	JobDepartmentSubRole,
	JobNotEnabledEmployee,
	SubRoleUpsertBody,
	SyncScheduledResponse,
	TimeOffMappingDeleteResult,
	TimeOffTypeMapping,
} from "../../../../../utils/models";

const HRIS_MAPPING_QUERY_KEY = "hris-mapping";

/**
 * Build the full ordered `jobIds` payload for a cert-mapping save: the (possibly new) first job —
 * the one the role string syncs as — followed by the row's current additional jobs (jobs[1:]),
 * dropping any that duplicate it. Every save site must send the complete list; the backend treats
 * the payload as the row's new state, so omitting the tail would wipe the additional jobs.
 */
export function buildJobIds(firstJobId: string, currentJobs: Job[]): string[] {
	return [
		firstJobId,
		...currentJobs
			.slice(1)
			.map((j) => j.id)
			.filter((id) => id !== firstJobId),
	];
}

// ───── Query hooks ─────

export function useDepartmentCertificationMapping(departmentId: string) {
	return useQuery<CertificationJobMapping[]>({
		queryKey: [HRIS_MAPPING_QUERY_KEY, "cert", departmentId],
		queryFn: async () => {
			const res = await backendService.getDepartmentCertJobMapping(departmentId);
			return res.data;
		},
		enabled: !!departmentId,
	});
}

export function useDepartmentEmploymentTypeMapping(departmentId: string) {
	return useQuery<EmploymentTypeMapping[]>({
		queryKey: [HRIS_MAPPING_QUERY_KEY, "employment", departmentId],
		queryFn: async () => {
			const res = await backendService.getDepartmentEmploymentTypeMapping(departmentId);
			return res.data;
		},
		enabled: !!departmentId,
	});
}

export function useDepartmentEmployeeStatusMapping(departmentId: string) {
	return useQuery<EmployeeStatusMapping[]>({
		queryKey: [HRIS_MAPPING_QUERY_KEY, "employee-status", departmentId],
		queryFn: async () => {
			const res = await backendService.getDepartmentEmployeeStatusMapping(departmentId);
			return res.data;
		},
		enabled: !!departmentId,
	});
}

export function useDepartmentTimeOffTypeMapping(departmentId: string) {
	return useQuery<TimeOffTypeMapping[]>({
		queryKey: [HRIS_MAPPING_QUERY_KEY, "time-off", departmentId],
		queryFn: async () => {
			const res = await backendService.getDepartmentTimeOffTypeMapping(departmentId);
			return res.data;
		},
		enabled: !!departmentId,
	});
}

/** Per-department Clipboard `job_department_sub_role` catalog rows; missing rows silently drop Clipboard shifts. */
export function useDepartmentSubRoles() {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<JobDepartmentSubRole[]>({
		queryKey: [HRIS_MAPPING_QUERY_KEY, "sub-roles", departmentId],
		queryFn: async () => {
			const res = await backendService.getDepartmentSubRoles(departmentId);
			return res.data;
		},
		enabled: !!departmentId,
	});
}

/**
 * Returns the flat list of raw HRIS employees for the given department.
 * Used to filter the Roles/Employment grids to keys that actually appear in raw data,
 * and to drive the Pretty-Mapping pane.
 */
export function useHrisRawEmployees(departmentId: string) {
	return useQuery<HrisRawEmployeeRow[]>({
		queryKey: [HRIS_MAPPING_QUERY_KEY, "raw-employees", departmentId],
		queryFn: async () => {
			const res = await backendService.getHrisRawEmployeesForDepartment(departmentId);
			return res.data;
		},
		enabled: !!departmentId,
	});
}

/**
 * Returns the distinct raw PTO event-type strings observed for the given department.
 * Drives the Time Off grid's unmapped/orphan detection — the PTO analog of useHrisRawEmployees.
 */
export function useHrisRawPtoEventTypes(departmentId: string) {
	return useQuery<string[]>({
		queryKey: [HRIS_MAPPING_QUERY_KEY, "raw-pto-event-types", departmentId],
		queryFn: async () => {
			const res = await backendService.getHrisRawPtoEventTypesForDepartment(departmentId);
			return res.data;
		},
		enabled: !!departmentId,
	});
}

/**
 * Returns an imperative fetcher for the count of synced time-offs a re-map would touch (DAT-200).
 * Called when the confirm dialog opens — the count comes from the actual external_time_off rows, which
 * differ from the 60-day column's raw-event volume because consecutive events merge into spans. Cached
 * briefly so reopening for the same raw string doesn't refetch.
 */
export function useTimeOffRemapPreviewFetcher(departmentId: string) {
	const queryClient = useQueryClient();
	return useCallback(
		(rawString: string) =>
			queryClient.fetchQuery<number>({
				queryKey: [HRIS_MAPPING_QUERY_KEY, "remap-preview", departmentId, rawString.trim().toLowerCase()],
				queryFn: async () => (await backendService.getTimeOffRemapPreview(departmentId, rawString)).data.count,
				staleTime: 30_000,
			}),
		[queryClient, departmentId],
	);
}

/**
 * Returns an imperative fetcher for the DAT-535 primary-job change preview: the active employees linked to
 * a role string and whether each one's first job would be overwritten by the new primary job. Called when
 * the primary job is edited in the Roles grid, before the confirm dialog opens. Keyed by role string + new
 * job id and cached briefly so reopening the same change doesn't refetch.
 */
export function useCertPrimaryJobChangePreviewFetcher(departmentId: string) {
	const queryClient = useQueryClient();
	return useCallback(
		(roleString: string, newJobId: string) =>
			queryClient.fetchQuery<CertPrimaryJobChangePreview>({
				queryKey: [HRIS_MAPPING_QUERY_KEY, "primary-job-change-preview", departmentId, roleString.trim().toLowerCase(), newJobId],
				queryFn: async () => (await backendService.getCertPrimaryJobChangePreview(departmentId, roleString, newJobId)).data,
				staleTime: 30_000,
			}),
		[queryClient, departmentId],
	);
}

/** Per-raw-type PTO counts over the last 60 days, keyed by trim+lowercased rawString (DAT-200). */
export function useHrisPtoTypeStats(departmentId: string) {
	return useQuery<HrisPtoTypeStats[]>({
		queryKey: [HRIS_MAPPING_QUERY_KEY, "pto-type-stats", departmentId],
		queryFn: async () => {
			const res = await backendService.getHrisPtoTypeStats(departmentId);
			return res.data;
		},
		enabled: !!departmentId,
	});
}

/**
 * Employees ACTIVE in this department whose PRIMARY job the department doesn't enable (DAT-834).
 *
 * Read-only: the two fixes are enabling the job (Settings → Jobs) and remapping the HRIS role
 * (Settings → HRIS ↔ InHouse), never an action on the employee. Both screens observe this one key, so
 * the tab badge and the two surfaces read the same list from one request.
 *
 * `enabled` is the CALLER's call rather than a default: the Jobs tab renders for a department that has
 * not been created yet, which has no id to query and no members to flag, while the HRIS pane only ever
 * renders for a saved one.
 */
export function useJobNotEnabledEmployees(departmentId: string, enabled: boolean) {
	return useQuery<JobNotEnabledEmployee[]>({
		queryKey: [HRIS_MAPPING_QUERY_KEY, "job-not-enabled", departmentId],
		queryFn: async () => {
			const res = await backendService.getJobNotEnabledEmployees(departmentId);
			return res.data;
		},
		enabled: enabled && !!departmentId,
	});
}

/** Per-employee activity stats (active flag + 30-day shift counts), keyed by employeeId (DAT-154). */
export function useHrisEmployeeShiftStats(departmentId: string) {
	return useQuery<HrisEmployeeShiftStats[]>({
		queryKey: [HRIS_MAPPING_QUERY_KEY, "employee-shift-stats", departmentId],
		queryFn: async () => {
			const res = await backendService.getHrisEmployeeShiftStats(departmentId);
			return res.data;
		},
	});
}

// ───── Cache update helpers ─────

/** Replaces a row with the same key in the cached list, or appends if not found. */
function upsertInCache<T>(queryClient: QueryClient, queryKey: QueryKey, row: T, getKey: (r: T) => string) {
	queryClient.setQueryData<T[]>(queryKey, (prev) => {
		if (!prev) return prev;
		const rowKey = getKey(row);
		const idx = prev.findIndex((r) => getKey(r) === rowKey);
		if (idx >= 0) {
			const next = [...prev];
			next[idx] = row;
			return next;
		}
		return [...prev, row];
	});
}

/** Applies a delete result — replace the row if `revertedTo` is set, else remove by `key`. */
function applyDeleteInCache<T>(queryClient: QueryClient, queryKey: QueryKey, result: { key: string; revertedTo: T | null }, getKey: (r: T) => string) {
	queryClient.setQueryData<T[]>(queryKey, (prev) => {
		if (!prev) return prev;
		if (result.revertedTo) {
			const revertedTo = result.revertedTo;
			return prev.map((r) => (getKey(r) === result.key ? revertedTo : r));
		}
		return prev.filter((r) => getKey(r) !== result.key);
	});
}

// ───── Employee re-sync notification (DAT-386) ─────

// Grid edits fire debounced per-row mutations in bursts; one re-sync snackbar per window is enough.
const EMPLOYEE_SYNC_SNACKBAR_SUPPRESS_MS = 30_000;
let lastEmployeeSyncSnackbarAt = 0;

type ShowSnackbarFn = (message: string, severity: "success" | "info" | "warning" | "error") => void;
type TrackFn = ReturnType<typeof useAnalytics>["track"];

/** Surfaces the employee re-sync the backend scheduled alongside a mapping change (DAT-386). */
function notifyEmployeeSync(
	employeeSync: SyncScheduledResponse | null | undefined,
	departmentId: string,
	track: TrackFn,
	showSnackbar: ShowSnackbarFn,
	t: TFunction,
) {
	if (!employeeSync) return;
	// Tracked on every response, before the snackbar suppression — burst edits still count individually.
	track("HRIS Mapping - employee resync scheduled", {
		department_id: departmentId,
		bumped_count: employeeSync.task_names.length,
		already_running_count: employeeSync.already_running_task_names.length,
		bumped_tasks: employeeSync.task_names.join(","),
		already_running_tasks: employeeSync.already_running_task_names.join(","),
	});
	const messageKey = employeeSync.task_names?.length
		? "hris.mapping.resync.scheduled"
		: employeeSync.already_running_task_names?.length
			? "integration.resync.already.running"
			: null;
	if (!messageKey) return;
	const now = Date.now();
	if (now - lastEmployeeSyncSnackbarAt < EMPLOYEE_SYNC_SNACKBAR_SUPPRESS_MS) return;
	lastEmployeeSyncSnackbarAt = now;
	showSnackbar(t(messageKey), "success");
}

// ───── Mutation hooks ─────

// `deactivateNurseIds` / `syncNurseIds` flip nurses off/on alongside the mapping change.
// `jobIds` always carries the row's FULL ordered job list — jobIds[0] is the job the role string
// syncs as, the rest are additional jobs (DAT-466); [] means Don't Sync. The backend diffs the
// tail against the stored value and mirrors additions/removals onto linked nurses.
type CertUpsertBody = {
	roleString: string;
	jobIds: string[];
	position: string;
	role: string | null;
	deactivateNurseIds?: number[];
	syncNurseIds?: number[];
};

export function useUpsertCertMapping(departmentId: string) {
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track } = useAnalytics();
	const { t } = useTranslation();
	const queryKey = [HRIS_MAPPING_QUERY_KEY, "cert", departmentId];
	return useMutation<CertificationJobMapping, Error, CertUpsertBody>({
		mutationFn: async (body) => {
			const res = await backendService.updateDepartmentCertJobMapping(departmentId, body);
			return res.data;
		},
		onSuccess: (row, body) => {
			// `employeeSync` is transport-only metadata — strip it so it doesn't linger stale in the list cache.
			const { employeeSync, ...cacheRow } = row;
			upsertInCache<CertificationJobMapping>(queryClient, queryKey, cacheRow, (r) => r.roleString);
			// Saving a mapping can add jobs, and new jobs start silent, so refetch.
			void invalidateAllJobNotificationQueries(queryClient);
			// De/reactivations flip the active flag — refetch stats so the popup/hover reflect it.
			if (body.deactivateNurseIds?.length || body.syncNurseIds) {
				queryClient.invalidateQueries({ queryKey: [HRIS_MAPPING_QUERY_KEY, "employee-shift-stats", departmentId] });
			}
			notifyEmployeeSync(employeeSync, departmentId, track, showSnackbar, t);
		},
	});
}

export function useDeleteCertMapping(departmentId: string) {
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track } = useAnalytics();
	const { t } = useTranslation();
	const queryKey = [HRIS_MAPPING_QUERY_KEY, "cert", departmentId];
	return useMutation<CertMappingDeleteResult, Error, string>({
		mutationFn: async (roleString) => {
			const res = await backendService.deleteDepartmentCertJobOverride(departmentId, roleString);
			return res.data;
		},
		onSuccess: (result) => {
			applyDeleteInCache<CertificationJobMapping>(queryClient, queryKey, result, (r) => r.roleString);
			void invalidateAllJobNotificationQueries(queryClient);
			notifyEmployeeSync(result.employeeSync, departmentId, track, showSnackbar, t);
		},
	});
}

type EmploymentUpsertBody = { rawString: string; employmentType: string };

export function useUpsertEmploymentMapping(departmentId: string) {
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track } = useAnalytics();
	const { t } = useTranslation();
	const queryKey = [HRIS_MAPPING_QUERY_KEY, "employment", departmentId];
	return useMutation<EmploymentTypeMapping, Error, EmploymentUpsertBody>({
		mutationFn: async (body) => {
			const res = await backendService.updateDepartmentEmploymentTypeMapping(departmentId, body);
			return res.data;
		},
		onSuccess: (row) => {
			// `employeeSync` is transport-only metadata — strip it so it doesn't linger stale in the list cache.
			const { employeeSync, ...cacheRow } = row;
			upsertInCache<EmploymentTypeMapping>(queryClient, queryKey, cacheRow, (r) => r.rawString);
			notifyEmployeeSync(employeeSync, departmentId, track, showSnackbar, t);
		},
	});
}

export function useDeleteEmploymentMapping(departmentId: string) {
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track } = useAnalytics();
	const { t } = useTranslation();
	const queryKey = [HRIS_MAPPING_QUERY_KEY, "employment", departmentId];
	return useMutation<EmploymentMappingDeleteResult, Error, string>({
		mutationFn: async (rawString) => {
			const res = await backendService.deleteDepartmentEmploymentTypeOverride(departmentId, rawString);
			return res.data;
		},
		onSuccess: (result) => {
			applyDeleteInCache<EmploymentTypeMapping>(queryClient, queryKey, result, (r) => r.rawString);
			notifyEmployeeSync(result.employeeSync, departmentId, track, showSnackbar, t);
		},
	});
}

type EmployeeStatusUpsertBody = { rawString: string; status: string };

export function useUpsertEmployeeStatusMapping(departmentId: string) {
	const queryClient = useQueryClient();
	const queryKey = [HRIS_MAPPING_QUERY_KEY, "employee-status", departmentId];
	return useMutation<EmployeeStatusMapping, Error, EmployeeStatusUpsertBody>({
		mutationFn: async (body) => {
			const res = await backendService.updateDepartmentEmployeeStatusMapping(departmentId, body);
			return res.data;
		},
		onSuccess: (row) => upsertInCache<EmployeeStatusMapping>(queryClient, queryKey, row, (r) => r.rawString),
	});
}

export function useDeleteEmployeeStatusMapping(departmentId: string) {
	const queryClient = useQueryClient();
	const queryKey = [HRIS_MAPPING_QUERY_KEY, "employee-status", departmentId];
	return useMutation<EmployeeStatusMappingDeleteResult, Error, string>({
		mutationFn: async (rawString) => {
			const res = await backendService.deleteDepartmentEmployeeStatusOverride(departmentId, rawString);
			return res.data;
		},
		onSuccess: (result) => applyDeleteInCache<EmployeeStatusMapping>(queryClient, queryKey, result, (r) => r.rawString),
	});
}

// `remapPrevious` only sent when the user opts to re-map the last 60 days of synced time-offs (DAT-200).
type TimeOffUpsertBody = { rawString: string; ptoType: string | null; reason?: string | null; remapPrevious?: boolean };

export function useUpsertTimeOffMapping(departmentId: string) {
	const queryClient = useQueryClient();
	const queryKey = [HRIS_MAPPING_QUERY_KEY, "time-off", departmentId];
	return useMutation<TimeOffTypeMapping, Error, TimeOffUpsertBody>({
		mutationFn: async (body) => {
			const res = await backendService.updateDepartmentTimeOffTypeMapping(departmentId, body);
			return res.data;
		},
		onSuccess: (row, body) => {
			upsertInCache<TimeOffTypeMapping>(queryClient, queryKey, row, (r) => r.rawString);
			// Re-mapping rewrites historical time-offs — refetch stats and drop the cached preview counts so
			// the 60-day column and any reopened confirm dialog stay accurate.
			if (body.remapPrevious) {
				queryClient.invalidateQueries({ queryKey: [HRIS_MAPPING_QUERY_KEY, "pto-type-stats", departmentId] });
				queryClient.invalidateQueries({ queryKey: [HRIS_MAPPING_QUERY_KEY, "remap-preview", departmentId] });
			}
		},
	});
}

export function useDeleteTimeOffMapping(departmentId: string) {
	const queryClient = useQueryClient();
	const queryKey = [HRIS_MAPPING_QUERY_KEY, "time-off", departmentId];
	return useMutation<TimeOffMappingDeleteResult, Error, string>({
		mutationFn: async (rawString) => {
			const res = await backendService.deleteDepartmentTimeOffTypeOverride(departmentId, rawString);
			return res.data;
		},
		onSuccess: (result) => applyDeleteInCache<TimeOffTypeMapping>(queryClient, queryKey, result, (r) => r.rawString),
	});
}

// ───── Sub-role mapping hooks ─────

export function useUpsertSubRole() {
	const queryClient = useQueryClient();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const queryKey = [HRIS_MAPPING_QUERY_KEY, "sub-roles", departmentId];
	return useMutation<JobDepartmentSubRole, Error, SubRoleUpsertBody>({
		mutationFn: async (body) => {
			const res = await backendService.updateDepartmentSubRole(departmentId, body);
			return res.data;
		},
		// A default toggle flips a sibling off server-side, so refetch rather than patch one cached row.
		onSuccess: () => queryClient.invalidateQueries({ queryKey }),
	});
}

export function useDeleteSubRole() {
	const queryClient = useQueryClient();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const queryKey = [HRIS_MAPPING_QUERY_KEY, "sub-roles", departmentId];
	return useMutation<void, Error, string>({
		mutationFn: async (rowId) => {
			await backendService.deleteDepartmentSubRole(departmentId, rowId);
		},
		onSuccess: (_void, rowId) => {
			queryClient.setQueryData<JobDepartmentSubRole[]>(queryKey, (prev) => (prev ? prev.filter((r) => r.id !== rowId) : prev));
		},
	});
}

// ───── Ignore-list hooks ─────

/**
 * Returns the ignore-list rows for the department: per-dept rows merged with global rows
 * (`departmentId === null`). The grid components match by category + rawValue to decide
 * whether to show the "Ignore" button or the "Unignore" affordance.
 */
export function useHrisIgnoredValues(departmentId: string) {
	return useQuery<HrisIgnoredValueRow[]>({
		queryKey: [HRIS_MAPPING_QUERY_KEY, "ignored", departmentId],
		queryFn: async () => {
			const res = await backendService.listHrisIgnoredValues({ departmentId, includeGlobal: true });
			return res.data;
		},
		enabled: !!departmentId,
	});
}

type CreateIgnoredValueBody = {
	category: HrisIgnoreCategory;
	rawValue: string;
	// Pass departmentId to scope to this dept; omit (or null) to create a global rule.
	departmentId?: string | null;
	source?: string | null;
};

export function useCreateHrisIgnoredValue(departmentId: string) {
	const queryClient = useQueryClient();
	const queryKey = [HRIS_MAPPING_QUERY_KEY, "ignored", departmentId];
	return useMutation<HrisIgnoredValueRow, Error, CreateIgnoredValueBody>({
		mutationFn: async (body) => {
			const res = await backendService.createHrisIgnoredValue(body);
			return res.data;
		},
		onSuccess: (row) => {
			queryClient.setQueryData<HrisIgnoredValueRow[]>(queryKey, (prev) => {
				if (!prev) return [row];
				if (prev.some((r) => r.id === row.id)) return prev;
				return [...prev, row];
			});
		},
	});
}

export function useDeleteHrisIgnoredValue(departmentId: string) {
	const queryClient = useQueryClient();
	const queryKey = [HRIS_MAPPING_QUERY_KEY, "ignored", departmentId];
	return useMutation<void, Error, number>({
		mutationFn: async (id) => {
			await backendService.deleteHrisIgnoredValue(id);
		},
		onSuccess: (_void, id) => {
			queryClient.setQueryData<HrisIgnoredValueRow[]>(queryKey, (prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
		},
	});
}
